import { useCallback } from "react";
import { useToast } from "@/components/system";
import {
  useUpdateActionItem,
  type ActionItem,
  type ActionState,
} from "@/hooks/use-action-items";
import { formatError } from "@/lib/format-error";

/**
 * BW20.1 PR-C — bundled mutation + 3-second undo toast for task actions.
 *
 * Returns three handlers any task surface (Today, /tasks list, /tasks
 * kanban, project drawer, /inbox triage) can wire into TaskCard:
 *
 *   - onCycleState(item, next): plain state change, no toast
 *   - onMarkDone(item): sets state='done' + 3s undo toast
 *   - onCancel(item): sets state='cancelled' + 3s undo toast
 *
 * The toast copy follows the State / Meaning / Next-move template per
 * CLAUDE.md adherence-neutral rules — never says "you failed" or
 * "missed". Undo restores the prior state from the closure.
 */
export function useTaskAction() {
  const update = useUpdateActionItem();
  const toast = useToast();

  const setStateWithUndo = useCallback(
    async (item: ActionItem, next: ActionState, copy: { state: string; meaning: string; nextMove: string }) => {
      const previous = item.state;
      try {
        await update.mutateAsync({ id: item.id, patch: { state: next } });
        toast.show(`${copy.state} ${copy.meaning} ${copy.nextMove}`, {
          durationMs: 3000,
          undo: {
            label: "Undo",
            onUndo: () => {
              void update
                .mutateAsync({ id: item.id, patch: { state: previous } })
                .catch((err) =>
                  toast.show(`Undo failed: ${formatError(err)}`),
                );
            },
          },
        });
      } catch (err) {
        toast.show(`Update failed: ${formatError(err)}`);
      }
    },
    [update, toast],
  );

  const onMarkDone = useCallback(
    (item: ActionItem) =>
      setStateWithUndo(item, "done", {
        state: "Marked done.",
        meaning: "Hidden from Today's list.",
        nextMove: "Tap Undo to restore.",
      }),
    [setStateWithUndo],
  );

  const onCancel = useCallback(
    (item: ActionItem) =>
      setStateWithUndo(item, "cancelled", {
        state: "Cancelled, doesn't apply anymore.",
        meaning: "Hidden from Today's list.",
        nextMove: "Tap Undo to restore.",
      }),
    [setStateWithUndo],
  );

  const onCycleState = useCallback(
    async (item: ActionItem, next: ActionState) => {
      // The two terminal states get the undo-toast treatment via the
      // dedicated handlers above. All other transitions are plain.
      if (next === "done") return onMarkDone(item);
      if (next === "cancelled") return onCancel(item);
      try {
        await update.mutateAsync({ id: item.id, patch: { state: next } });
      } catch (err) {
        toast.show(`Update failed: ${formatError(err)}`);
      }
    },
    [update, toast, onMarkDone, onCancel],
  );

  return { onCycleState, onMarkDone, onCancel };
}
